import React from "react";
import PropTypes from "prop-types";
import { Switch, Select } from "antd";
import {
  useScheduleTimeFrame,
  useTimeChangeHandlers,
  useDaySelectionHandlers,
  useToggleHandlers,
} from "../../hooks/useScheduleTimeFrame";
import "./ScheduleTimeFrame.css";

const DAYS_OF_WEEK = [
  { label: "Su", value: "Sunday" },
  { label: "Mo", value: "Monday" },
  { label: "Tu", value: "Tuesday" },
  { label: "We", value: "Wednesday" },
  { label: "Th", value: "Thursday" },
  { label: "Fr", value: "Friday" },
  { label: "Sa", value: "Saturday" },
];

function ScheduleTimeFrame({ initialValues, onChange, disabled }) {
  const state = useScheduleTimeFrame({ onScheduleChange: onChange, initialValues });

  const { handleFromTimeChange, handleToTimeChange } = useTimeChangeHandlers({
    fromTime: state.fromTime,
    toTime: state.toTime,
    setFromTime: state.setFromTime,
    setToTime: state.setToTime,
    notifyChange: state.notifyChange,
  });

  const { handleDayToggle, handleSelectAll, handleClearAll } = useDaySelectionHandlers({
    selectedDays: state.selectedDays,
    setSelectedDays: state.setSelectedDays,
    notifyChange: state.notifyChange,
  });

  const { handleDaysToggle, handleTimeToggle } = useToggleHandlers({
    setDaysEnabled: state.setDaysEnabled,
    setTimeEnabled: state.setTimeEnabled,
    notifyChange: state.notifyChange,
  });

  const buildHourOptions = (hours) =>
    hours.map((h) => ({ value: h, label: h }));

  const buildMinuteOptions = (minutes) =>
    minutes.map((m) => ({ value: m, label: m }));

  const buildPeriodOptions = (periods) =>
    periods.map((p) => ({ value: p, label: p }));

  return (
    <div className="schedule-timeframe">
      {/* Days of Week section */}
      <div className="schedule-timeframe__section">
        <div className="schedule-timeframe__section-header">
          <span
            className={
              disabled || !state.daysEnabled
                ? "schedule-timeframe__section-label schedule-timeframe__section-label--disabled"
                : "schedule-timeframe__section-label"
            }
          >
            Days of Week
          </span>
          <Switch
            checked={state.daysEnabled}
            onChange={handleDaysToggle}
            disabled={disabled}
            style={{ backgroundColor: state.daysEnabled && !disabled ? "#008331" : "#d8dada" }}
            size="small"
          />
        </div>

        {state.daysEnabled && (
          <div className="schedule-timeframe__day-grid">
            {DAYS_OF_WEEK.map((day) => (
              <button
                key={day.value}
                type="button"
                className={
                  state.selectedDays.includes(day.value)
                    ? "schedule-timeframe__day-btn schedule-timeframe__day-btn--selected"
                    : "schedule-timeframe__day-btn"
                }
                onClick={() => handleDayToggle(day.value)}
                disabled={disabled}
                aria-label={day.value}
                aria-pressed={state.selectedDays.includes(day.value)}
              >
                {day.label}
              </button>
            ))}
            <div className="schedule-timeframe__day-actions">
              <button
                type="button"
                className="schedule-timeframe__text-link"
                onClick={handleSelectAll}
                disabled={disabled}
              >
                All
              </button>
              <button
                type="button"
                className="schedule-timeframe__text-link"
                onClick={handleClearAll}
                disabled={disabled}
              >
                Clear
              </button>
            </div>
            {state.selectedDays.length === 0 && (
              <div className="schedule-timeframe__day-error">
                Please select at least one day
              </div>
            )}
          </div>
        )}

        {!state.daysEnabled && (
          <div className="schedule-timeframe__summary">{state.daysStatusText}</div>
        )}
      </div>

      {/* Time of Day section */}
      <div className="schedule-timeframe__section">
        <div className="schedule-timeframe__section-header">
          <span
            className={
              disabled || !state.timeEnabled
                ? "schedule-timeframe__section-label schedule-timeframe__section-label--disabled"
                : "schedule-timeframe__section-label"
            }
          >
            Time of Day
          </span>
          <Switch
            checked={state.timeEnabled}
            onChange={handleTimeToggle}
            disabled={disabled || !state.daysEnabled}
            style={{ backgroundColor: state.timeEnabled && !disabled && state.daysEnabled ? "#008331" : "#d8dada" }}
            size="small"
          />
        </div>

        {state.timeEnabled && (
          <>
            <div className="schedule-timeframe__time-row">
              <span
                className={
                  disabled
                    ? "schedule-timeframe__time-label schedule-timeframe__time-label--disabled"
                    : "schedule-timeframe__time-label"
                }
              >
                From
              </span>
              <Select
                className="schedule-timeframe__time-select shipment-form-fields"
                value={state.fromTime.hour}
                onChange={(val) => handleFromTimeChange("hour", val)}
                options={buildHourOptions(state.hours)}
                disabled={disabled}
              />
              <span className="schedule-timeframe__time-separator">:</span>
              <Select
                className="schedule-timeframe__time-select shipment-form-fields"
                value={state.fromTime.minute}
                onChange={(val) => handleFromTimeChange("minute", val)}
                options={buildMinuteOptions(state.minutes)}
                disabled={disabled}
              />
              <Select
                className="schedule-timeframe__time-select shipment-form-fields"
                value={state.fromTime.period}
                onChange={(val) => handleFromTimeChange("period", val)}
                options={buildPeriodOptions(state.periods)}
                disabled={disabled}
              />
            </div>

            <div className="schedule-timeframe__time-row">
              <span
                className={
                  disabled
                    ? "schedule-timeframe__time-label schedule-timeframe__time-label--disabled"
                    : "schedule-timeframe__time-label"
                }
              >
                To
              </span>
              <Select
                className="schedule-timeframe__time-select shipment-form-fields"
                value={state.toTime.hour}
                onChange={(val) => handleToTimeChange("hour", val)}
                options={buildHourOptions(state.validToHours)}
                disabled={disabled}
              />
              <span className="schedule-timeframe__time-separator">:</span>
              <Select
                className="schedule-timeframe__time-select shipment-form-fields"
                value={state.toTime.minute}
                onChange={(val) => handleToTimeChange("minute", val)}
                options={buildMinuteOptions(state.validToMinutes)}
                disabled={disabled}
              />
              <Select
                className="schedule-timeframe__time-select shipment-form-fields"
                value={state.toTime.period}
                onChange={(val) => handleToTimeChange("period", val)}
                options={buildPeriodOptions(state.validToPeriods)}
                disabled={disabled}
              />
            </div>
          </>
        )}

        {!state.timeEnabled && state.daysEnabled && state.timeStatusText && (
          <div className="schedule-timeframe__summary">{state.timeStatusText}</div>
        )}
      </div>
    </div>
  );
}

ScheduleTimeFrame.propTypes = {
  initialValues: PropTypes.shape({
    daysEnabled: PropTypes.bool,
    timeEnabled: PropTypes.bool,
    days: PropTypes.arrayOf(PropTypes.string),
    fromTime: PropTypes.shape({
      hour: PropTypes.string,
      minute: PropTypes.string,
      period: PropTypes.string,
    }),
    toTime: PropTypes.shape({
      hour: PropTypes.string,
      minute: PropTypes.string,
      period: PropTypes.string,
    }),
  }),
  onChange: PropTypes.func,
  disabled: PropTypes.bool,
};

ScheduleTimeFrame.defaultProps = {
  initialValues: null,
  onChange: null,
  disabled: false,
};

export default ScheduleTimeFrame;
